'use client';

import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';

/**
 * Pont entre le navigateur (API WebAuthn, via `@simplewebauthn/browser`) et les options / réponses
 * échangées avec le core. Le core (bibliothèque Yubico) sérialise les options sous la forme
 * `{ publicKey: {…} }` ; SimpleWebAuthn attend le contenu de `publicKey`.
 */

type WithPublicKey = { publicKey: Record<string, unknown> };

const unwrap = (options: unknown): unknown => {
  if (options && typeof options === 'object' && 'publicKey' in options) {
    return (options as WithPublicKey).publicKey;
  }
  return options;
};

/** Cet appareil peut-il créer une clé d'accès (Face ID, Touch ID, Windows Hello…) ? */
export async function canEnrollPasskey(): Promise<boolean> {
  if (typeof window === 'undefined' || !browserSupportsWebAuthn()) {
    return false;
  }
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && browserSupportsWebAuthn();
}

export async function passkeyAutofillSupported(): Promise<boolean> {
  if (!passkeysSupported()) {
    return false;
  }
  try {
    return await browserSupportsWebAuthnAutofill();
  } catch {
    return false;
  }
}

/**
 * Enrôlement : `navigator.credentials.create()` avec les options du core.
 * Avec `silent`, mise à niveau automatique (mediation conditionnelle) : le gestionnaire de mots
 * de passe crée la clé sans aucune fenêtre s'il vient de servir à se connecter avec le mot de
 * passe Sowefund ; sinon il lève une erreur (traitée comme « pas de mise à niveau »).
 */
export async function createPasskey(options: unknown, silent = false) {
  return startRegistration({
    optionsJSON: unwrap(options) as Parameters<
      typeof startRegistration
    >[0]['optionsJSON'],
    useAutoRegister: silent,
  });
}

/**
 * Connexion : `navigator.credentials.get()`. Avec `autofill`, la demande reste en attente en
 * arrière-plan et la clé est proposée dans l'autocomplétion du champ e-mail
 * (`autocomplete="username webauthn"`) ; sans, la fenêtre système s'ouvre tout de suite.
 */
export async function getPasskey(options: unknown, autofill = false) {
  return startAuthentication({
    optionsJSON: unwrap(options) as Parameters<
      typeof startAuthentication
    >[0]['optionsJSON'],
    useBrowserAutofill: autofill,
  });
}

/** Annule une demande d'autocomplétion en attente (changement d'écran, clic sur le bouton). */
export function cancelPendingPasskey() {
  try {
    WebAuthnAbortService.cancelCeremony();
  } catch {
    /* rien en attente */
  }
}

/** Erreur « l'utilisateur a fermé la fenêtre » : pas une erreur à afficher. */
export function isPasskeyCancelled(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'NotAllowedError' || error.name === 'AbortError')
  );
}

/** Le trousseau a déjà une clé pour ce compte (`excludeCredentials`) : refus normal du navigateur. */
export function isPasskeyAlreadyRegistered(error: unknown): boolean {
  return error instanceof Error && error.name === 'InvalidStateError';
}
